/**
 * @file FilterSheetButton.tsx
 * @description Кнопка «Фильтры · N» и нижняя шторка с фильтрами списка для
 * телефона и планшета (phase19.31, разбор A §4 п.4 — общий компонент).
 *
 * На узком экране в строку помещаются только поиск и пилюли; остальные
 * фильтры (стадия, источник, флажки) уходят в шторку. Число на кнопке —
 * сколько фильтров в шторке сейчас включено: человек видит, что список
 * сужен, даже не открывая её. Фильтры применяются сразу (без «Применить»);
 * «Сбросить» снимает только то, что лежит в шторке.
 *
 * ```tsx
 * <FilterSheetButton count={2} onReset={reset}>
 *   <SelectField … />
 * </FilterSheetButton>
 * ```
 */

import { SlidersHorizontal } from "lucide-react";
import { useState, type ReactNode } from "react";

import { Button } from "./button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./sheet";

interface Props {
  /** Сколько фильтров шторки включено; 0 — без числа. */
  count: number;
  /** Снять фильтры шторки; не задано — кнопки «Сбросить» нет. */
  onReset?: () => void;
  /** Заголовок шторки. */
  title?: string;
  children: ReactNode;
}

export function FilterSheetButton({ count, onReset, title = "Фильтры", children }: Props) {
  const [open, setOpen] = useState(false);
  const label = count > 0 ? `Фильтры · ${count}` : "Фильтры";
  return (
    <>
      <Button variant="outline" className="shrink-0" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <SlidersHorizontal className="size-4" aria-hidden />
        {label}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="gap-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <SheetHeader>
            <SheetTitle className="type-card-title">{title}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-3">{children}</div>
          <div className="flex gap-2">
            {onReset ? (
              <Button variant="outline" className="flex-1" onClick={onReset} disabled={count === 0}>
                Сбросить
              </Button>
            ) : null}
            <Button variant="default" className="flex-1" onClick={() => setOpen(false)}>
              Показать
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
